
// export enum LogLevel {
//   Debug,
//   Info,
//   Warning,
//   Error,
//   Critical
// }

import { tryCaptureStackTrace } from "../errors/error-stack-handling";
import {LogLevel} from "../logging";
import {JsonValue} from "../utils";
import {HttpErrorResponse} from "@angular/common/http";

export interface IMessageCodeBase {
  /** Null when the API sent the error without its code on purpose: there is no code to show, and none to report. */
  readonly code: string|null;
  /**
   * The user-facing message: plain text, `\n` line breaks and `{support:key}` placeholders. Only ever set from
   * an API response body -- the client's own copy lives in `ClientErrorDisplays`.
   */
  readonly description: string|null;
  /** The user-facing title, from the API response body. */
  readonly title?: string|null;
  readonly devDescription?: string|null;
}


type MessageCodeWithOptions = {
  cause?: unknown;
  data?: JsonValue|null;
} & ({
  useCauseStack?: boolean;
} | {
  useStack?: Error['stack'];
})

export interface IMessageCode extends IMessageCodeBase, Error {
  readonly title: string|null;
  readonly level: LogLevel;
  readonly traceId?: string;
  readonly data?: JsonValue|null;
  readonly meta: Record<string, unknown>;
  throw(): never;
  with(opt: MessageCodeWithOptions): IMessageCode;

  /***
    * Formats the message with the provided arguments
    * @param fn function/method/constructor to capture the stack from
    * @returns The self object. Just a convenience for chaining
    */
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  captureStackFrom(fn: Function): IMessageCode;
}

export class MessageCode extends Error implements IMessageCode {

  with(opt: MessageCodeWithOptions): IMessageCode {
    let obj = new MessageCode(this.code, {devDescription: this.devDescription}, this.level);
    obj = Object.assign(obj, {
      title: this.title,
      description: this.description,
      data: opt.data ?? this.data,
      traceId: this.traceId,
      meta: {
        ...this.meta
      },
    });

    if (this.stack) {
      obj.stack = this.stack;
    }
    if (this.cause) {
      obj.cause = this.cause;
    }

    if (opt.cause !== undefined && opt.cause !== null) {
      obj.cause = opt.cause;
      if ('useCauseStack' in opt && opt.useCauseStack) {
        if (opt.cause instanceof Error) {
          obj.stack = opt.cause.stack;
        } else if (opt.cause instanceof HttpErrorResponse && opt.cause.error instanceof Error) {
          obj.stack = opt.cause.error.stack;
        }
      } else if ('useStack' in opt && opt.useStack) {
        obj.stack = opt.useStack;
      }
    }

    return obj;

  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  public captureStackFrom(fn: Function): IMessageCode {
    return tryCaptureStackTrace(this, fn);
  }

  public throw(): never {
    throw this;
  }

  public readonly data?: JsonValue|null;
  public readonly traceId?: string;
  public readonly meta = {};
  public readonly code: string|null;
  public readonly title: string|null = null;
  public readonly description: string|null = null;
  public readonly devDescription: string|null;
  public readonly level: LogLevel;

  /**
   * Takes only the developer label. The user-facing title and message come from the API response
   * (`ensureInitializedMessageCode`), so a static code here can never carry copy of its own.
   */
  constructor(code: string|null, options: {devDescription?: string|null}|null, level: LogLevel) {
    const devDescription = options?.devDescription ?? null;
    super(`[${code}]: ${devDescription ?? 'Unknown Message Code'}`);
    this.name = code ?? this.name;
    this.code = code;
    this.devDescription = devDescription;
    this.level = level;

  }
  // The BSR codes: raised in the browser, so the client owns them and writes their developer label.
  // `isIgnoredError` also keys on the `BSR` prefix to decide what reaches Sentry.
  public static get BSR0000(): IMessageCode { return new MessageCode('BSR0000', {devDescription: 'Unknown Browser Exception'}, LogLevel.Error); }
  public static get Unknown_Browser_Exception(): IMessageCode { return MessageCode.BSR0000; }

  public static get BSR0002(): IMessageCode { return new MessageCode('BSR0002', {devDescription: 'Browser Request Dropped'}, LogLevel.Error); }
  public static get Browser_Request_Dropped(): IMessageCode { return MessageCode.BSR0002; }

  public static get BSR0003(): IMessageCode { return new MessageCode('BSR0003', {devDescription: 'Invalid Parameters Received'}, LogLevel.Error); }
  public static get Invalid_Parameters_Received(): IMessageCode { return MessageCode.BSR0003; }

  /**
   * Raised when the browser cannot decode an image the user selected -- most often a HEIC/HEIF photo that
   * neither the native decoder nor the WASM fallback could read. Deliberately a `BSR` code: `isIgnoredError`
   * drops every non-`BSR` code as server-reported, so anything else here would never reach Sentry.
   **/
  public static get BSR0005(): IMessageCode { return new MessageCode('BSR0005', {devDescription: 'Image Decode Failed'}, LogLevel.Error); }
  public static get Browser_Image_Decode_Failed(): IMessageCode { return MessageCode.BSR0005; }

  // The server's codes that the client names somewhere -- to raise one itself, or to branch on it. They
  // carry the code and nothing else: the copy and any developer label come from the response body, which
  // `ensureInitializedMessageCode` reads. Listing one here buys nothing on its own, so the rest of the
  // API's catalogue is deliberately absent: an unlisted code arrives and renders exactly the same way,
  // because `FromCode` returning null just means the code is built from the body instead.
  //
  // ATH0002/ATH0003 pick the inline-vs-stop OTP path; ATH0005, ITK0005, ITK0011 and ITK0012 are also
  // raised client-side with their own copy; ITK0004 chooses a button; NTF0001-4 route to the inline
  // device error. The full catalogue lives in the API (`MindPath/Exceptions/MessageCodes.g.cs`).
  public static get ITK0004(): IMessageCode { return new MessageCode('ITK0004', null, LogLevel.Error); }
  public static get ITK0005(): IMessageCode { return new MessageCode('ITK0005', null, LogLevel.Error); }
  public static get ITK0011(): IMessageCode { return new MessageCode('ITK0011', null, LogLevel.Error); }
  public static get ITK0012(): IMessageCode { return new MessageCode('ITK0012', null, LogLevel.Error); }
  public static get ATH0002(): IMessageCode { return new MessageCode('ATH0002', null, LogLevel.Error); }
  public static get ATH0003(): IMessageCode { return new MessageCode('ATH0003', null, LogLevel.Error); }
  public static get ATH0005(): IMessageCode { return new MessageCode('ATH0005', null, LogLevel.Error); }
  public static get NTF0001(): IMessageCode { return new MessageCode('NTF0001', null, LogLevel.Error); }
  public static get NTF0002(): IMessageCode { return new MessageCode('NTF0002', null, LogLevel.Error); }
  public static get NTF0003(): IMessageCode { return new MessageCode('NTF0003', null, LogLevel.Error); }
  public static get NTF0004(): IMessageCode { return new MessageCode('NTF0004', null, LogLevel.Error); }


  public static FromCode(code: string|null): IMessageCode|null {

    if (code !== null && code in MessageCode) {
      const found  = MessageCode[code as keyof typeof MessageCode];
      if (isMessageCode(found)) {
        return found;
      }
    }
    return null;
  }
  public static Dupe(msg: IMessageCode): IMessageCode {
    const obj = new MessageCode(msg.code, {devDescription: msg.devDescription}, msg.level);
    return Object.assign(obj, {
      title: msg.title,
      description: msg.description,
      data: msg.data,
      traceId: msg.traceId,
      meta: msg.meta,
    });
  }
}

/** A non-blank string, or null. */
const textOrNull = (value: unknown): string|null => typeof value === 'string' && value.trim().length ? value : null;

export const ensureInitializedMessageCode = (obj: IMessageCodeBase|IMessageCode): IMessageCode => {

  if (obj instanceof MessageCode) {
    return obj;
  }

  // An API body carries the user message as `message`. A MessageCode that lost its prototype still has
  // `meta`, and its `message` is the developer-facing Error message, so only `description` counts there.
  const isMessageCodeShaped = 'meta' in obj;
  const description = isMessageCodeShaped
    ? textOrNull(obj.description)
    : textOrNull('message' in obj ? obj.message : undefined) ?? textOrNull(obj.description);
  const devDescription = 'devDescription' in obj ? textOrNull(obj.devDescription) : null;

  // an unknown code is kept: the API can add codes before the client lists them
  const code = MessageCode.FromCode(obj.code)
    ?? new MessageCode(obj.code, {devDescription}, 'level' in obj && typeof obj.level === 'number' ? obj.level : LogLevel.Error);

  // The server owns the label for its own codes, and the listed ones carry none of their own. Taking the
  // body's is safe -- it already crossed the wire -- and it is what Sentry fingerprints on. A BSR code
  // keeps the client's label when the body has nothing to say.
  const label = devDescription ?? code.devDescription;

  return Object.assign(code, {
    title: textOrNull('title' in obj ? obj.title : undefined),
    description,
    devDescription: label,
    // the Error message is the developer's view of the code, so keep it in step with the label
    message: `[${code.code}]: ${label ?? 'Unknown Message Code'}`,
    data: 'data' in obj ? obj.data : null,
    traceId: 'traceId' in obj ? obj.traceId : '',
    meta: 'meta' in obj ? obj.meta : {},
  }).with({
    cause: 'cause' in obj ? obj.cause : undefined,
    useCauseStack: 'cause' in obj,
    useStack: 'stack' in obj ? obj.stack : undefined,
  });
}

/** Every code the API or the client raises: a 3-6 letter category and four digits (`ITK0001`, `OLNITK0002`). */
const MessageCodePattern = /^[A-Z]{3,6}\d{4}$/;

export const isBaseMessageCode = (obj: unknown): obj is IMessageCodeBase => {
  if (typeof obj !== 'object' || !obj) {
    return false;
  }
  if (obj instanceof MessageCode) {
    return true;
  }

  // a null code is one the API withheld; a missing one is not a message code at all
  return 'code' in obj && (obj.code === null || (typeof obj.code === 'string' && MessageCodePattern.test(obj.code)));
}
export const isMessageCode = (obj: unknown): obj is IMessageCode => {
  if (!isBaseMessageCode(obj)) {
    return false;
  }
  return 'meta' in obj;
}
